import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CarriersService } from '@app/pages/delivery-service/services';
import { BoxService } from '@app/services/boxes/box.service';
import { OrderEvents } from '@app/services/order/order.model';
import { OrderService } from '@app/services/orders/order.service';
import { ShopService } from '@app/services/shops/shop.service';
import { CarrierTypesCustomFields, ServiceLevelDisplayNames } from '@pdqdev/pdq-shared/dist/CarrierData';
import { TimeUnits } from '@utils/times-utils';
import moment from 'moment';

import { Subject, Subscription } from 'rxjs';
import { ApiService } from '../../../../../core/services/api/api.service';
import { FulfillService } from '../../fulfill.service';
import { getOrderSplitIndicesString } from '@utils/order-split-utils';
import { isServiceLevelsEquals } from '@pdqdev/pdq-shared/dist/utils';

@Component({
  selector: 'fulfill-scan-package-dialog',
  templateUrl: './fulfill-scan-package-dialog.html',
  styleUrls: ['./fulfill-scan-package-dialog.scss'],
  standalone: false,
})
export class FulfillScanPackageDialog implements OnInit, OnDestroy {
  scannedToken;
  scannedBox;
  order;
  orderItemId;
  banner = null;
  shopBoxes;
  isLoading = false;
  displayTags: string[];
  isMultiParcel: boolean;
  currentBatch;
  batchSubscription: Subscription;
  destroy$ = new Subject();
  timeoutIdentifier;
  orderNumberText: string;

  constructor(
    public dialogRef: MatDialogRef<FulfillScanPackageDialog>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private shopService: ShopService,
    private boxService: BoxService,
    private orderService: OrderService,
    private fulfillService: FulfillService,
    private carriersService: CarriersService,
    private apiService: ApiService,
  ) {
    this.order = data.order;
    this.scannedToken = data.scannedSku;
  }

  ngOnInit() {
    this.isMultiParcel = this.fulfillService.isShopOnMultiParcelMode();
    this.currentBatch = this.fulfillService.getActiveBatch;
    this.initPrepareBatchListener();
    this.orderNumberText =
      this.order.orderNumber + (this.order.splitted ? getOrderSplitIndicesString(this.order, 'view', true) : '');
    if (this.isMultiParcel && this.order.orderItems?.length) {
      this.orderItemId = this.order.orderItems[0]?.id;
    }
    this.shopService.shopDefaultBoxes$.subscribe(({ activeBoxes, inactiveBoxes }) => {
      this.shopBoxes = [...activeBoxes];
      if (this.scannedToken) {
        this.scanBox();
      }
    });

    const shopTags = this.fulfillService.shopMetadata?.packageScanFulfillTags?.split(',');
    const orderTags = this.order.orderItems.reduce(
      (tags, item) => tags.concat(item.product?.productTags ?? []),
      this.order.orderTags ?? [],
    );
    this.displayTags = shopTags?.filter((tagName) => orderTags.some((tag) => tag.name == tagName));
  }
  ngOnDestroy(): void {
    if (this.batchSubscription) {
      this.batchSubscription.unsubscribe();
    }
    this.clearTimeout();
    this.destroy$.next(true);
  }

  onClose(fulfill) {
    this.dialogRef.close({ fulfilled: fulfill, updatedBox: this.order.box });
  }

  onCancel() {
    this.onClose(false);
  }

  onFulfill() {
    this.onClose(true);
  }

  scanBox() {
    if (!this.scannedToken) return;

    this.scannedToken = this.scannedToken.toLowerCase().replaceAll(/\./g, '-');
    const currentBoxToken = this.order.box?.ecommerceToken?.toLowerCase();
    console.log(`scanned value: ${this.scannedToken}, current box: ${currentBoxToken}`);

    if (currentBoxToken == this.scannedToken || currentBoxToken + currentBoxToken == this.scannedToken) {
      this.onFulfill();
    } else if (this.scannedBox?.ecommerceToken?.toLowerCase() == this.scannedToken) {
      if (
        this.scannedBox?.length == this.order.box?.length &&
        this.scannedBox?.width == this.order.box?.width &&
        this.scannedBox?.height == this.order.box?.height
      ) {
        this.switchPackageWithoutResend();
      } else {
        this.resendToCarrier();
      }
    } else {
      this.scannedBox = this.shopBoxes?.find((box) => box.ecommerceToken?.toLowerCase() == this.scannedToken);
      if (this.scannedBox) {
        this.banner = {
          variant: 'attention',
          title: "Package SKU doesn't belong",
          bullets: [
            'You scanned ' + this.boxService.utils.getBoxName(this.scannedBox),
            'Scan again to confirm package selection',
          ],
        };
      } else {
        this.banner = {
          variant: 'danger',
          title: "Something went wrong - package SKU doesn't exist",
          bullets: ['Check your package barcode or enter package SKU again manually'],
        };
      }
      this.scannedToken = '';
    }
  }

  onFailedBeforeResendToCarrier() {
    this.banner = {
      variant: 'danger',
      title: 'Failed to get rates and repurchase the label with the new package',
      bullets: ['We apologize, something went wrong! please close this window to start over'],
    };
    this.isLoading = false;
  }

  onFaiedInResendToCarrier() {
    this.banner = {
      variant: 'danger',
      title: `Failed to repurchase the shipping label due to a technical issue with the carrier!
   Order #${this.orderNumberText} is now back in the Assign screen`,
    };
    this.isLoading = false;
  }

  onTimeout() {
    this.banner = {
      variant: 'attention',
      title: 'Shipping label repurchase is taking more time than expected...',
    };
  }

  resendToCarrier() {
    this.isLoading = true;
    this.carriersService
      .getCarrierCustomFieldValue(
        this.order?.orderBatch?.selectedCarrier?.type?.name,
        CarrierTypesCustomFields.SUPPORTS_RATES,
      )
      .subscribe(
        async (carrierSupportsRates) => {
          let serviceLevelId = null;
          if (carrierSupportsRates) {
            const rateObj = await this.getRate();
            if (!rateObj) {
              console.log(`cannot get rates for order ${this.order.id}`);
              this.onFailedBeforeResendToCarrier();
              return;
            }
            serviceLevelId = rateObj.carrierServiceLevelId;
          }
          this.switchPackageAndResend(serviceLevelId);
        },
        (err) => {
          console.log(err);
          this.onFailedBeforeResendToCarrier();
        },
      );
  }

  switchPackageWithoutResend() {
    const referId = { orderId: this.order.id, orderItemId: this.orderItemId };
    const payload = { box: this.scannedBox };
    this.orderService.switchOrderPackage(payload, referId).subscribe(
      (updatedOrder) => {
        this.order = updatedOrder;
        this.fulfillService.setActiveOrder(updatedOrder);
        this.onFulfill();
      },
      (err) => {
        console.log(err);
        this.onFailedBeforeResendToCarrier();
      },
    );
  }

  switchPackageAndResend(serviceLevelId: any) {
    const referId = { orderId: this.order.id, orderItemId: this.orderItemId };
    const payload = { box: this.scannedBox, serviceLevelId };
    this.orderService.switchOrderPackageAndCreateShipment(payload, referId).subscribe(
      (updatedOrder) => {
        this.order = updatedOrder;
        this.fulfillService.setActiveOrder(updatedOrder);
        this.timeoutIdentifier = setTimeout(async () => {
          this.order = await this.orderService.getOrder(this.order.id);
          this.fulfillService.setActiveOrder(this.order);
          const halfMinuteAgo = moment().subtract(30, 'seconds');
          const assignToCarrierEvent = this.order.dateEvent?.find(
            (event) =>
              event?.event?.includes(OrderEvents.ASSIGN_TO_CARRIER) && moment(event?.eventDate).isAfter(halfMinuteAgo),
          );
          const carrierStatusChangedEvent = this.order.dateEvent?.find(
            (event) =>
              event?.event?.includes(OrderEvents.CARRIER_STATUS_CHANGED) &&
              moment(event?.eventDate).isAfter(halfMinuteAgo),
          );
          if (this.order.batchId == this.currentBatch.id && assignToCarrierEvent && carrierStatusChangedEvent) {
            this.onFulfill();
          } else {
            this.onTimeout();
          }
        }, TimeUnits._MINUTE_IN_MS / 2);
      },
      (err) => {
        console.log(err);
        this.onFailedBeforeResendToCarrier();
      },
    );
  }
  private findServiceRate(orderRates: any, serviceLevelToken: string) {
    return orderRates.rates.find(
      (rate) =>
        rate?.carrierServiceLevelInternalToken === serviceLevelToken ||
        rate?.carrierServiceLevelId?.courier_id === serviceLevelToken,
    );
  }
  async getRate() {
    try {
      const { id, orderBatch, deliveryTransaction } = this.order;
      const orderChanges: any = {
        orderId: id,
      };
      if (this.isMultiParcel) {
        orderChanges.orderItems = [{ orderItemId: this.orderItemId, box: this.scannedBox }];
      } else {
        orderChanges.box = this.scannedBox;
      }
      const ratesOptions = await this.orderService.getOrdersRates(
        [id],
        orderBatch?.id,
        this.scannedBox.id,
        undefined,
        undefined,
        { changes: { orders: [orderChanges] } },
      );

      const currentOrderRates = ratesOptions?.rateInfo?.totalServiceLevelsRatesData?.ordersRatesOptions?.find(
        (val) => val?.orderId === id,
      );
      const recommendedOrderRate = ratesOptions?.rateInfo?.totalServiceLevelsRatesData?.selectedServiceLevelsRates
        ?.find((val) => isServiceLevelsEquals(val.serviceLevel, ServiceLevelDisplayNames.PDQ_CHOISE))
        ?.ratesPerProviders?.find((val) => val.isOptimal)
        ?.ordersRatesDetails?.find((val) => val.orderId == id);
      if (!currentOrderRates?.rates?.length) {
        console.log(`order ${id} - got rates with length 0`);
        return false;
      }
      const prevSlToken = deliveryTransaction?.serviceLevelToken;
      const slToken = this.fulfillService.shopMetadata.ruleBasedRepurchase
        ? (
            await this.apiService
              .get(`rate-cards/service-by-rules`, {
                params: { boxId: this.scannedBox.id, orderId: id },
              })
              .toPromise()
          ).serviceLevel?.name
        : prevSlToken;
      let finalRatesObj;
      if (slToken) {
        finalRatesObj = this.findServiceRate(currentOrderRates, slToken);
        if (!finalRatesObj && prevSlToken !== slToken) {
          /** Service level was chosed by shop rules, but the we didn't receive rate for this service level, then using previous service level */
          finalRatesObj = this.findServiceRate(currentOrderRates, prevSlToken);
        }
      } else {
        // if no support for compare service level tokens
        finalRatesObj =
          this.findServiceRate(currentOrderRates, prevSlToken) ?? recommendedOrderRate ?? currentOrderRates.rates[0];
      }
      if (!finalRatesObj) {
        console.log(`order ${id} - has no final rates obj, rates: ${JSON.stringify(currentOrderRates.rates)}`);
        return false;
      }

      return finalRatesObj;
    } catch (e) {
      console.log(`Error while get rates for order ${this.order?.id}: ${JSON.stringify(e)}`);
    }
  }

  private initPrepareBatchListener() {
    this.batchSubscription = this.fulfillService.onPrepareBatchFinish.subscribe((batch) => {
      if (batch.id == this.currentBatch.id) {
        this.clearTimeout();
        const updatedOrder = batch.orders.find((order) => order.id == this.order.id);
        if (updatedOrder) {
          if (updatedOrder.box?.id == this.order.box?.id) {
            this.onFulfill();
          }
        } else {
          this.onFaiedInResendToCarrier();
        }
      }
    });
  }

  private clearTimeout() {
    if (this.timeoutIdentifier) {
      clearTimeout(this.timeoutIdentifier);
    }
  }
}
